import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Sparkles, Send, FileText, ChevronRight } from 'lucide-react';
import type { User, Vacancy } from '../types';
import type { ProposalRow } from '../data/clientEngagementMock';
import { parsePortalClientProposal, parsePortalThreadFromCvSnapshot } from '../utils/portalProposalThread';
import { formatNlDateTime } from './clientEngagementCenter/engagementLabels';
import EngagementCandidateModal from './clientEngagementCenter/EngagementCandidateModal';
import {
  supabase_append_portal_thread_message,
  supabase_recruiter_save_portal_proposal_snapshot,
} from '../services/supabaseService';

export interface VacancyClientPortalCandidateEditorProps {
  vacancy: Vacancy;
  application: any;
  proposal: ProposalRow;
  user: User;
  cvSnapshot: Record<string, unknown> | null;
  onSaved: () => void;
}

function skillsFromCandidate(c: any): string[] {
  if (!c) return [];
  if (Array.isArray(c.skill_set)) return c.skill_set.map(String).filter(Boolean).slice(0, 14);
  const ai = c?.ai_parsed_data;
  if (ai?.skills && Array.isArray(ai.skills)) return ai.skills.map(String).filter(Boolean).slice(0, 14);
  return [];
}

const VacancyClientPortalCandidateEditor: React.FC<VacancyClientPortalCandidateEditorProps> = ({
  vacancy,
  application,
  proposal,
  user: _user,
  cvSnapshot,
  onSaved,
}) => {
  const appId = String(application?.id || proposal.proposalId || '').trim();
  const cand = application?.candidates || application?.candidate || {};
  const portal = useMemo(() => parsePortalClientProposal(application?.cv_snapshot), [application?.cv_snapshot]);

  const [summary, setSummary] = useState('');
  const [matchNotes, setMatchNotes] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState('');
  const [salaryIndication, setSalaryIndication] = useState('');
  const [matchScore, setMatchScore] = useState<number>(0);
  const [threadDraft, setThreadDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [threadBusy, setThreadBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    setSummary(portal?.summary?.trim() || proposal.recruiterPortalSummary || '');
    setMatchNotes(portal?.match_notes?.trim() || '');
    setAvailableFrom(portal?.available_from?.trim() || '');
    setHoursPerWeek(portal?.hours_per_week?.trim() || '');
    setSalaryIndication(portal?.salary_indication?.trim() || proposal.salaryIndication || '');
    const msApp = typeof application?.match_score === 'number' ? application.match_score : undefined;
    const msPortal = typeof portal?.match_score === 'number' ? portal.match_score : undefined;
    const ms = msApp ?? msPortal ?? proposal.matchScore;
    setMatchScore(Number.isFinite(ms) ? Math.round(Number(ms)) : 0);
  }, [application?.id, application?.match_score, application?.cv_snapshot, portal, proposal]);

  const thread = useMemo(() => {
    const fromSnap = parsePortalThreadFromCvSnapshot(cvSnapshot ?? application?.cv_snapshot);
    if (fromSnap.length > 0) return fromSnap;
    return proposal.portalThread ?? [];
  }, [cvSnapshot, application?.cv_snapshot, proposal.portalThread]);

  const runAiMatch = useCallback(async () => {
    setAiBusy(true);
    setToast(null);
    try {
      const { performRealMatch } = await import('../services/geminiService');
      const c = cand;
      const ai = c?.ai_parsed_data || {};
      const profile = {
        name: String(c.name || proposal.name || ''),
        role: String(c.role || proposal.role || ''),
        summary: String(c.cv_summary || ai.summary || '').trim() || String(c.original_cv_text || '').slice(0, 800),
        skills: skillsFromCandidate(c),
        timeline: Array.isArray(ai.work_experience) ? ai.work_experience : [],
      };
      const anyVac = vacancy as Record<string, unknown>;
      const vac = {
        title: String(vacancy.title || ''),
        description: String(vacancy.description || anyVac?.finalVacancyContent || ''),
        data: (vacancy.data || {}) as Record<string, unknown>,
      };
      const r = await performRealMatch(profile, vac, undefined);
      const score = typeof r?.score === 'number' ? Math.max(0, Math.min(100, Math.round(r.score))) : 0;
      setMatchScore(score);
      const reason = String(r?.reason || '').trim();
      setSummary((prev) => (prev.trim() ? prev : reason));
      const pros: string[] = Array.isArray(r?.pros) ? r.pros.map((x: unknown) => String(x).trim()).filter(Boolean) : [];
      const cons: string[] = Array.isArray(r?.cons) ? r.cons.map((x: unknown) => String(x).trim()).filter(Boolean) : [];
      const block = [
        pros.length ? `Pluspunten:\n${pros.map((p) => `• ${p}`).join('\n')}` : '',
        cons.length ? `Aandachtspunten:\n${cons.map((p) => `• ${p}`).join('\n')}` : '',
      ]
        .filter(Boolean)
        .join('\n\n');
      setMatchNotes((prev) => (prev.trim() ? prev : block));
      setToast('AI-match toegepast — controleer en pas aan waar nodig.');
    } catch (e) {
      console.error(e);
      setToast('AI-match mislukt. Controleer netwerk / AI-instellingen.');
    } finally {
      setAiBusy(false);
    }
  }, [cand, proposal.name, proposal.role, vacancy]);

  const handleSave = async () => {
    if (!appId) return;
    setSaving(true);
    setToast(null);
    try {
      const { error } = await supabase_recruiter_save_portal_proposal_snapshot(appId, {
        matchScore,
        summary,
        matchNotes,
        availableFrom,
        hoursPerWeek,
        salaryIndication,
      });
      if (error) throw error;
      setToast('Opgeslagen.');
      onSaved();
    } catch (e) {
      console.error(e);
      setToast('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendThread = async () => {
    const t = threadDraft.trim();
    if (!appId || !t) return;
    setThreadBusy(true);
    try {
      const { error } = await supabase_append_portal_thread_message(appId, t, 'recruiter');
      if (error) throw error;
      setThreadDraft('');
      setToast('Bericht toegevoegd aan klantportaal-thread.');
      onSaved();
    } catch (e) {
      console.error(e);
      setToast('Bericht versturen mislukt.');
    } finally {
      setThreadBusy(false);
    }
  };

  const pipelineLabel = String(application?.status || proposal.pipelineStatus || '—');

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-100/80">
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50/90 to-white px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-sm font-black text-white shadow-md">
              {proposal.initials}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{proposal.name}</h3>
              <p className="text-sm font-medium text-slate-500">{proposal.vacancyTitle}</p>
              <p className="mt-1 text-[11px] font-semibold text-slate-600">
                Pipeline (database): <span className="text-slate-900">{pipelineLabel}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
              Match %
              <input
                type="number"
                min={0}
                max={100}
                value={matchScore}
                onChange={(e) => setMatchScore(Number(e.target.value))}
                className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm font-bold text-slate-900 outline-none focus:border-indigo-400"
              />
            </label>
            <button
              type="button"
              disabled={aiBusy}
              onClick={() => void runAiMatch()}
              className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-violet-900 transition hover:bg-violet-100 disabled:opacity-50"
            >
              {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              AI-match
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4 px-5 py-5 sm:px-6">
        {toast ? <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">{toast}</p> : null}

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Samenvatting voor de klant</label>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={4}
            className="mt-1 w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            placeholder="Korte pitch — vul zelf in of gebruik AI-match."
          />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Match CV ↔ vacature (incl. pro/con)</label>
          <textarea
            value={matchNotes}
            onChange={(e) => setMatchNotes(e.target.value)}
            rows={6}
            className="mt-1 w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            placeholder="Pluspunten en aandachtspunten — AI-match vult dit voorbeeldgewijs."
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-400">Beschikbaar vanaf</label>
            <input
              type="date"
              value={availableFrom?.slice(0, 10) || ''}
              onChange={(e) => setAvailableFrom(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-400">Uren / week</label>
            <input
              type="text"
              value={hoursPerWeek}
              onChange={(e) => setHoursPerWeek(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-400">Salarisindicatie</label>
            <input
              type="text"
              value={salaryIndication}
              onChange={(e) => setSalaryIndication(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setProfileOpen(true)}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50/40 px-3 py-3 text-left text-sm font-bold text-indigo-900 transition hover:bg-indigo-100/50"
        >
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4 shrink-0" />
            CV &amp; kandidaatprofiel (voorbeeld)
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-indigo-600" />
        </button>

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Bericht naar klant (portaal-thread)</p>
          <p className="mt-1 text-xs text-slate-500">Zichtbaar voor de klant in het klantportaal bij dit dossier.</p>
          {thread.length ? (
            <ul className="mt-3 max-h-40 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-sm">
              {thread.map((m, i) => (
                <li key={`${m.at}-${i}`} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                  <span className="text-[10px] font-bold uppercase text-slate-400">
                    {m.author === 'client' ? 'Klant' : 'Recruiter'} · {formatNlDateTime(m.at)}
                  </span>
                  <p className="mt-1 whitespace-pre-wrap text-slate-800">{m.text}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-slate-500">Nog geen berichten.</p>
          )}
          <textarea
            value={threadDraft}
            onChange={(e) => setThreadDraft(e.target.value)}
            rows={3}
            placeholder="Schrijf een update of vraag aan de klant…"
            className="mt-3 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
          />
          <button
            type="button"
            disabled={threadBusy || !threadDraft.trim()}
            onClick={() => void handleSendThread()}
            className="mt-2 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {threadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Verstuur naar klant
          </button>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-black disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Opslaan
          </button>
        </div>
      </div>

      <EngagementCandidateModal open={profileOpen} proposal={proposal} cvSnapshot={cvSnapshot} onClose={() => setProfileOpen(false)} />
    </article>
  );
};

export default VacancyClientPortalCandidateEditor;
